import React from "react";
import { Card, Row, Col, Statistic, Table, Tag, Tooltip } from "antd";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip as RechartsTooltip,
} from "recharts";
import {
  LinkOutlined,
  UserOutlined,
  BarChartOutlined,
} from "@ant-design/icons";

const COLORS = [
  "#1890FF",
  "#52C41A",
  "#722ED1",
  "#FA8C16",
  "#EB2F96",
  "#13C2C2",
  "#2F54EB",
  "#FADB14",
];

const ClickAnalytics = ({ clickData }) => {
  if (!clickData) return null;

  // Create pieData from the array structure instead of object
  const pieData = clickData.linkPerformance.map((link, index) => ({
    name: link.url.length > 30 ? link.url.substring(0, 30) + "..." : link.url,
    value: link.totalClicks,
    fullUrl: link.url,
  }));

  const columns = [
    {
      title: "Link URL",
      dataIndex: "url",
      key: "url",
      width: "40%",
      render: (url) => (
        <Tooltip title="Click to open">
          <a href={url} target="_blank" rel="noopener noreferrer">
            {url}
          </a>
        </Tooltip>
      ),
    },
    {
      title: "Total Clicks",
      dataIndex: "totalClicks",
      key: "totalClicks",
      sorter: (a, b) => a.totalClicks - b.totalClicks,
      render: (total) => (
        <Tag color="blue">
          <BarChartOutlined /> {total}
        </Tag>
      ),
    },
    {
      title: "Unique Clicks",
      dataIndex: "uniqueClicks",
      key: "uniqueClicks",
      sorter: (a, b) => a.uniqueClicks - b.uniqueClicks,
      render: (unique) => (
        <Tag color="purple">
          <UserOutlined /> {unique}
        </Tag>
      ),
    },
    {
      title: "Click Rate",
      key: "rate",
      render: (_, record) => {
        // Ensure we don't divide by zero
        const totalRecipients = clickData.summary.totalRecipients || 1;
        return (
          <Tag color="cyan">
            {((record.uniqueClicks / totalRecipients) * 100).toFixed(1)}%
          </Tag>
        );
      },
      sorter: (a, b) => {
        const totalRecipients = clickData.summary.totalRecipients || 1;
        return (
          a.uniqueClicks / totalRecipients - b.uniqueClicks / totalRecipients
        );
      },
    },
  ];

  // Use the array structure directly for tableData
  const tableData = clickData.linkPerformance.map((link) => ({
    key: link.url,
    url: link.url,
    totalClicks: link.totalClicks,
    uniqueClicks: link.uniqueClicks,
    // We're removing the lastClick column since that data doesn't exist
  }));

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div
          style={{
            background: "white",
            padding: "10px",
            border: "1px solid #ccc",
            borderRadius: "4px",
          }}
        >
          <p style={{ margin: 0 }}>
            <strong>URL:</strong> {data.fullUrl}
          </p>
          <p style={{ margin: "5px 0 0" }}>
            <strong>Clicks:</strong> {data.value}
          </p>
        </div>
      );
    }
    return null;
  };

  // Calculate values that might be missing in the data
  const totalClicks =
    clickData.summary.totalClicks ||
    clickData.linkPerformance.reduce((sum, link) => sum + link.totalClicks, 0);

  const uniqueClickers =
    clickData.summary.uniqueClicks ||
    clickData.linkPerformance.reduce((sum, link) => sum + link.uniqueClicks, 0);

  const totalRecipients =
    clickData.summary.totalRecipients || uniqueClickers || 1;

  const averageClicksPerClicker =
    uniqueClickers > 0 ? (totalClicks / uniqueClickers).toFixed(2) : 0;

  return (
    <Row gutter={[24, 24]}>
      <Col xs={24} xl={8}>
        <Card bordered={false}>
          <Statistic
            title="Total Clicks"
            value={totalClicks}
            prefix={<LinkOutlined />}
            valueStyle={{ color: "#1890ff" }}
          />
          <div className="mt-4">
            <small>Across all links in the campaign</small>
          </div>
        </Card>
      </Col>
      <Col xs={24} xl={8}>
        <Card bordered={false}>
          <Statistic
            title="Unique Clickers"
            value={uniqueClickers}
            prefix={<UserOutlined />}
            valueStyle={{ color: "#52c41a" }}
          />
          <div className="mt-4">
            <small>
              {((uniqueClickers / totalRecipients) * 100).toFixed(1)}% of total
              recipients
            </small>
          </div>
        </Card>
      </Col>
      <Col xs={24} xl={8}>
        <Card bordered={false}>
          <Statistic
            title="Average Clicks per Clicker"
            value={
              averageClicksPerClicker ||
              clickData.patterns?.averageClicksPerUser ||
              0
            }
            prefix={<BarChartOutlined />}
            precision={2}
            valueStyle={{ color: "#722ed1" }}
          />
        </Card>
      </Col>
      <Col xs={24} lg={12}>
        <Card title="Click Distribution" bordered={false}>
          <div style={{ height: "400px" }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={120}
                  label={({
                    cx,
                    cy,
                    midAngle,
                    innerRadius,
                    outerRadius,
                    value,
                    index,
                  }) => {
                    const RADIAN = Math.PI / 180;
                    const radius =
                      25 + innerRadius + (outerRadius - innerRadius);
                    const x = cx + radius * Math.cos(-midAngle * RADIAN);
                    const y = cy + radius * Math.sin(-midAngle * RADIAN);
                    return (
                      <text
                        x={x}
                        y={y}
                        fill={COLORS[index % COLORS.length]}
                        textAnchor={x > cx ? "start" : "end"}
                        dominantBaseline="central"
                      >
                        {`${((value / totalClicks) * 100).toFixed(1)}%`}
                      </text>
                    );
                  }}
                >
                  {pieData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Legend
                  layout="horizontal"
                  verticalAlign="bottom"
                  align="center"
                />
                <RechartsTooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </Col>
      <Col xs={24} lg={12}>
        <Card title="Link Performance" bordered={false}>
          <Table
            columns={columns}
            dataSource={tableData}
            pagination={false}
            scroll={{ y: 400 }}
            size="small"
          />
        </Card>
      </Col>
    </Row>
  );
};

export default ClickAnalytics;
